import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Modal, Popover, Segmented, Tag, Tooltip } from "antd";
import { DownOutlined, FilterOutlined, SearchOutlined } from "@ant-design/icons";
import ApiService from "../../core/service/api-service";
import ResponsiveSelect from "../../core/components/ResponsiveSelect";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import { matchesSearch } from "../../core/utils/text-search";
import { useIsSmallScreen } from "../../core/utils/useMediaQuery";
import StudyTypeEnum from "../../enums/StudyTypeEnum";
import HospitalGroupSelectBox from "../hospital-group/HospitalGroupSelectBox";
import HospitalSelectBox from "../hospital/HospitalSelectBox";
import StudyListSelect from "../study-list/StudyListSelect";
import InstitutionSelect from "../user/InstitutionSelect";
import UserSelectBox from "../user/UserSelectBox";
import "./quick-filter-bar.css";

/*
 * "Diğer filtreler": hızlı filtre çubuğunda kendi düğmesi olmayan (bugün sol panelde duran) filtreler.
 * Panel, etiket satırı ve sayaç alan tanımlarıyla (fields) çalışır; tanımlar sayfaya özgü
 * (bkz. study-extra-filters.js, report-extra-filters.js). Uzun vadede sol panelin yerini alacak — alan
 * eklenirken sol paneldeki karşılığıyla aynı filtre anahtarını ve aynı değer biçimini kullan
 * (sol panel uygulanan filtreyi izleyip kendini eşitliyor).
 *
 * Alan tanımı:
 *   key          filtre anahtarı
 *   label        i18n anahtarı ya da (can) => anahtar; can('YETKI') izin sorar
 *   kind         text   → tek metin
 *                list   → virgül/boşluk/satırla ayrılmış çok değer (filtrede dizi)
 *                select → sabit seçenekler (options; multiple: çoklu)
 *                lookup → id listesi, adları sunucudan (source: hospitals | hospitalGroups | lists | institutions)
 *                user   → tek kullanıcı id'si, adı sunucudan (source: readableUsers | writableUsers; userType: read | write)
 *                assign → atanan kullanıcı; pickPermission varsa kullanıcı listesinden + '-1' atanmamış,
 *                         yoksa yalnız "Bana atananlar"
 *                custom → sayfanın kendi kontrolü: renderControl({ value, onChange, t }); ortak türlere
 *                         uymayan alanlar için (ortak dosyaya dokunmadan). Etiket değeri için format verilmeli.
 *   empty        etiketten kaldırınca yazılan değer; varsayılanı olan alanlarda varsayılan (ör. isDeleted: false)
 *   allOption    select: başa "Tümü" seçeneği ekler (filtrede null). Varsayılanı null olmayan alanlar için;
 *                onlarda boş seçim varsayılana döndüğünden "Tümü" başka yoldan seçilemiyor.
 *   permissions  biri yeterli; yoksa alan panelde görünmez (sol paneldeki koşulun aynısı)
 *   group        panelde altında durduğu başlık (FILTER_GROUPS). Verilmezse panel başlıksız düz ızgara çizer.
 *                Alanlar grup başlığıyla da aranır; gruplar ilk alanlarının sırasıyla dizilir.
 *   work         iş listesi alanı: elle değişince hazır filtre etiketi (quickFilters) düşer
 *   isActive(filter) / toDraft(filter) / toFilter(value) / clearChanges   alanın özel biçimi varsa
 *   format(filter, t)   etiketin tooltip'inde gösterilen değer (her türde varsayılanı ezer)
 */

export const STUDY_TYPE_OPTIONS = [
    { value: StudyTypeEnum.ROUTINE, label: 'Routine' },
    { value: StudyTypeEnum.INPATIENT, label: 'Inpatient' },
    { value: StudyTypeEnum.EMERGENCY, label: 'Emergency' },
    { value: StudyTypeEnum.COMPARATIVE_EXAMINATION, label: 'Comparative Examination' },
];

export const STUDY_STATUS_OPTIONS = [
    { value: 0, label: 'Not Completed' },
    { value: 1, label: 'Timeout, Reports not confirmed' },
    { value: 2, label: 'Completed' },
    { value: 3, label: 'Reports not confirmed' },
];

// Panel başlıkları (i18n anahtarı). Alan tanımındaki `group` bunlardan biridir.
export const FILTER_GROUPS = {
    PATIENT: 'QF_GROUP_PATIENT',
    SCOPE: 'QF_GROUP_SCOPE',
    WORK: 'QF_GROUP_WORK',
    PEOPLE: 'QF_GROUP_PEOPLE',
    OTHER: 'QF_GROUP_OTHER',
};

export const yesNo = (yes, no) => [{ value: true, label: yes }, { value: false, label: no }];

// "Tümü" seçeneğinin panel içindeki değeri: seçim kutusunda null "seçim yok" sayılıp varsayılana döndüğünden
// ayrı bir değer gerekiyor; filtreye null olarak yazılır (bkz. allOption).
const ALL = '__all__';

// Ad çözümlemesi için listeler: yalnız o an gereken kaynak, bileşen başına bir kez çekilir.
const LOOKUP_LOADERS = {
    hospitals: () => ApiService.UserManager.GetHospitalsOfUser(),
    hospitalGroups: () => ApiService.HospitalGroup.GetList(),
    lists: () => ApiService.StudyList.GetList(),
    institutions: () => ApiService.UserManager.GetInstitutionsOfUser(),
    readableUsers: () => ApiService.UserManager.GetReadableUsers(),
    writableUsers: () => ApiService.UserManager.GetWritableUsers(),
};

// Seçim kutusu kendi listesini çeken bileşen olan kaynaklar (sol paneldekilerle aynı bileşenler).
const LOOKUP_CONTROLS = {
    hospitalGroups: HospitalGroupSelectBox,
    hospitals: HospitalSelectBox,
    lists: StudyListSelect,
    institutions: InstitutionSelect,
};

// Dar ekranda panel Popover yerine modalda açılır, seçim kutuları da modal seçiciye (ModalSelect) döner:
// antd açılır listesi dokunmatik klavyenin altında kalıyor, uzun listede kaydırmak zorlaşıyordu.
// İkisi AYNI eşikte değişmeli — Popover içinden açılan modal seçici dışarı tık sayılıp paneli kapatabilir.
const MOBILE_BREAKPOINT = 992;

export const isEmpty = (v) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

// Sol panellerdeki ayrıştırmayla aynı: virgül, noktalı virgül ya da boşluk ayırır; boşlar atılır.
export const splitList = (v) => {
    if (Array.isArray(v))
        return v.map(s => `${s}`.trim()).filter(Boolean);
    return `${v ?? ''}`.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
};

const usePermissionCheck = () => {
    const { hasPermission } = useUser();
    return (name) => hasPermission(PERMISSIONS[name]);
};

// En çok iki seçenekli tekil seçim (Evet/Hayır türü) açılır liste yerine "Tümü | A | B" düğme grubu:
// seçili değer açmadan görünür, alan diğer kutuların kalabalığından ayrışır.
const isSegmented = (field) => field.kind === 'select' && !field.multiple && field.options.length <= 2;

// Varsayılanı olan tekil alan (isDeleted: false, isReportable: true): varsayılandayken kullanıcı seçmiş gibi
// görünmesin diye panelde "Varsayılan" notu alır.
const hasDefault = (field) => field.empty !== null && field.empty !== undefined && !Array.isArray(field.empty);

const labelOf = (field, can) => (typeof field.label === 'function' ? field.label(can) : field.label);

const canSee = (field, can) => !field.permissions || field.permissions.some(can);

export const isFieldActive = (field, filter) => {
    if (field.isActive)
        return field.isActive(filter);

    const v = filter[field.key];
    if (Array.isArray(v))
        return v.some(x => !isEmpty(x) && x !== -1);
    // "Tümü" de varsayılandan farklı bir seçim: etiket olarak görünmeli.
    if (field.allOption && isEmpty(v))
        return true;
    // Varsayılanı olan tekil alan (isDeleted: false, isReportable: true) varsayılandayken filtre sayılmaz.
    if (field.empty !== null && field.empty !== undefined && !Array.isArray(field.empty) && v === field.empty)
        return false;
    return !isEmpty(typeof v === 'string' ? v.trim() : v);
};

const removalChanges = (field) => ({
    ...(field.clearChanges || { [field.key]: field.empty }),
    // İş listesi alanı elle değişti: hazır filtre etiketi artık doğru değil.
    ...(field.work ? { quickFilters: undefined } : {}),
});

// Filtre → panel taslağı: çok değerli metin alanları kutuda virgülle birleşik gösterilir.
const draftValueOf = (field, filter) => {
    if (field.toDraft)
        return field.toDraft(filter);

    const v = filter[field.key];
    if (field.kind === 'list')
        return Array.isArray(v) ? v.join(', ') : (v ?? '');
    if (field.kind === 'lookup' || field.multiple)
        return Array.isArray(v) ? v : (isEmpty(v) ? [] : [v]);
    if (field.kind === 'text')
        return v ?? '';
    if (field.allOption && isEmpty(v))
        return ALL;
    return v ?? null;
};

// Panel taslağı → filtre değişiklikleri: sol paneldeki biçimle aynı.
const changesOf = (field, value) => {
    if (field.toFilter)
        return field.toFilter(value);

    switch (field.kind) {
        case 'text':
            return { [field.key]: `${value ?? ''}`.trim() || undefined };
        case 'list':
            return { [field.key]: splitList(value) };
        case 'lookup':
            return { [field.key]: value || [] };
        default:
            if (field.multiple)
                return { [field.key]: value || [] };
            if (value === ALL)
                return { [field.key]: null };
            return { [field.key]: value === undefined ? null : value };
    }
};

const clearedDraftValue = (field) => {
    if (field.kind === 'list' || field.kind === 'text')
        return '';
    if (field.kind === 'lookup' || field.multiple)
        return [];
    return field.empty ?? null;
};

const useLookups = (sources) => {
    const [lookups, setLookups] = useState({});
    const requested = useRef(new Set());
    const mounted = useRef(true);

    useEffect(() => () => {
        mounted.current = false;
    }, []);

    const key = [...new Set(sources.filter(Boolean))].sort().join('|');

    useEffect(() => {
        key.split('|').filter(Boolean).forEach(async (source) => {
            if (requested.current.has(source))
                return;
            requested.current.add(source);

            const res = await LOOKUP_LOADERS[source]();
            if (!mounted.current || !res?.success)
                return;

            const items = res.data || [];
            const labelKey = source.endsWith('Users') ? 'visibleName' : 'name';
            setLookups(prev => ({
                ...prev,
                [source]: { items, map: new Map(items.map(x => [x.id, x[labelKey]])) },
            }));
        });
    }, [key]);

    return lookups;
};

const formatValue = (field, filter, t, lookups, userId) => {
    if (field.format)
        return field.format(filter, t);

    const v = filter[field.key];
    const nameOf = (id) => lookups[field.source]?.map.get(id) ?? id;

    switch (field.kind) {
        case 'text':
            return `${v}`.trim();
        case 'list':
            return splitList(draftValueOf(field, filter)).join(', ');
        case 'lookup':
            return (Array.isArray(v) ? v : [v]).map(nameOf).join(', ');
        case 'select':
            if (field.allOption && isEmpty(v))
                return t('All');
            return (Array.isArray(v) ? v : [v])
                .filter(x => !isEmpty(x) && x !== -1)
                .map(x => {
                    const option = field.options.find(o => o.value === x);
                    return option ? t(option.label) : x;
                })
                .join(', ');
        case 'assign':
            if (`${v}` === '-1')
                return t('Not Assigned');
            if (`${v}`.includes(','))
                return t('QF_ASSIGN_FREE_OR_MINE');
            return lookups[field.source]?.map.get(v) ?? (v === userId ? t('Assigned Me') : v);
        case 'user':
            return nameOf(v);
        default:
            return Array.isArray(v) ? v.join(', ') : `${v}`;
    }
};

/**
 * Çubuğun altındaki "Diğer filtreler" satırı: fields içinden etkin olanlar etiket olur; üzerine gelince değer
 * görünür, × ile o filtre kaldırılır.
 */
export function ExtraFilterTags({ fields, filter, apply }) {
    const { t } = useTranslation();
    const { user } = useUser();
    const can = usePermissionCheck();

    const active = fields.filter(field => isFieldActive(field, filter));
    const lookups = useLookups(active.map(field => field.source));

    if (active.length === 0)
        return null;

    return (
        <div className="qf-summary">
            <span className="qf-summary-label">{t('Other filters')}:</span>
            {active.map(field => (
                <Tooltip
                    key={field.key}
                    title={(
                        <span>
                            <strong>{t(labelOf(field, can))}:</strong> {formatValue(field, filter, t, lookups, user?.data?.id)}
                        </span>
                    )}
                >
                    <Tag
                        className="qf-tag"
                        closable
                        onClose={(e) => {
                            // antd etiketi kendisi gizlemesin: filtre uygulanınca satır zaten yeniden çizilir.
                            e.preventDefault();
                            apply(removalChanges(field));
                        }}
                    >
                        {t(labelOf(field, can))}
                    </Tag>
                </Tooltip>
            ))}
        </div>
    );
}

function ExtraFiltersPanel({ fields: allFields, filter, apply, onDone }) {
    const { t } = useTranslation();
    const { user } = useUser();
    const can = usePermissionCheck();
    const isMobile = useIsSmallScreen(MOBILE_BREAKPOINT);
    // Parmakla kullanımda kontroller normal boyda; masaüstü panelinde yoğun (small).
    const controlSize = isMobile ? 'middle' : 'small';

    const [draft, setDraft] = useState(() => Object.fromEntries(
        allFields.map(field => [field.key, draftValueOf(field, filter)])
    ));
    const [hasHospitals, setHasHospitals] = useState(true);
    const [query, setQuery] = useState('');

    const fields = allFields.filter(field => canSee(field, can)
        && (field.source !== 'hospitals' || hasHospitals));

    const pickableAssign = fields.filter(field => field.kind === 'assign' && field.pickPermission && can(field.pickPermission));
    const lookups = useLookups(pickableAssign.map(field => field.source));

    const set = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));

    const applyDraft = () => {
        const changes = Object.assign({}, ...fields.map(field => changesOf(field, draft[field.key])));
        const workChanged = fields.some(field => field.work
            && JSON.stringify(changes[field.key] ?? null) !== JSON.stringify(filter[field.key] ?? null));
        if (workChanged)
            changes.quickFilters = undefined;

        apply(changes);
        onDone();
    };

    const clearDraft = () => setDraft(prev => ({
        ...prev,
        ...Object.fromEntries(fields.map(field => [field.key, clearedDraftValue(field)])),
    }));

    const selectProps = (field) => ({
        size: controlSize,
        allowClear: true,
        maxTagCount: 'responsive',
        modalOnMobile: true,
        mobileBreakpoint: MOBILE_BREAKPOINT,
        modalTitle: t(labelOf(field, can)),
        value: draft[field.key],
        onChange: (value) => set(field.key, value ?? clearedDraftValue(field)),
    });

    // Etkinlik uygulanmış filtreye değil TASLAĞA göre: kullanıcı neyi değiştirdiğini "Uygula"dan önce görsün.
    const isDraftActive = (field) => isFieldActive(field, { ...filter, ...changesOf(field, draft[field.key]) });
    const isAtDefault = (field) => hasDefault(field) && draft[field.key] === field.empty;
    const labelText = (field) => t(labelOf(field, can));

    // Arama yalnız GÖSTERİMİ süzer; uygula/temizle görünmeyen alanlar dahil hepsine uygulanır.
    const shown = fields.filter(field => matchesSearch(labelText(field), query)
        || (field.group && matchesSearch(t(field.group), query)));

    const sections = [];
    shown.forEach(field => {
        const key = field.group ?? '';
        let section = sections.find(x => x.key === key);
        if (!section)
            sections.push(section = { key, fields: [] });
        section.fields.push(field);
    });

    const renderSegmented = (field) => {
        const value = draft[field.key];
        const current = value === null || value === undefined || value === ALL
            ? ALL
            : `o${field.options.findIndex(o => o.value === value)}`;
        const option = (key, label) => ({ value: key, label: <span title={label}>{label}</span> });

        return (
            <Segmented
                size={controlSize}
                block
                value={current}
                options={[
                    option(ALL, t('All')),
                    ...field.options.map((o, i) => option(`o${i}`, t(o.label))),
                ]}
                // "Tümü": varsayılanı olan alanda ALL (filtrede null), diğerlerinde doğrudan null.
                onChange={(v) => set(field.key, v === ALL
                    ? (field.allOption ? ALL : null)
                    : field.options[Number(v.slice(1))].value)}
            />
        );
    };

    const renderControl = (field) => {
        if (isSegmented(field))
            return renderSegmented(field);

        switch (field.kind) {
            case 'custom':
                return field.renderControl({
                    value: draft[field.key],
                    onChange: (value) => set(field.key, value),
                    t,
                });
            case 'lookup': {
                const Control = LOOKUP_CONTROLS[field.source];
                return (
                    <Control
                        {...selectProps(field)}
                        {...(field.source === 'hospitals'
                            ? { onData: (data) => setHasHospitals((data?.length ?? 0) > 0) }
                            : {})}
                    />
                );
            }
            case 'text':
            case 'list':
                return (
                    <Input
                        size={controlSize}
                        allowClear
                        value={draft[field.key]}
                        placeholder={field.kind === 'list' ? t('Separate values with comma, space or new line') : undefined}
                        onChange={(e) => set(field.key, e.target.value)}
                        onPressEnter={applyDraft}
                    />
                );
            case 'user':
                return <UserSelectBox type={field.userType} {...selectProps(field)} />;
            case 'assign': {
                const options = field.pickPermission && can(field.pickPermission)
                    ? [
                        { value: '-1', label: t('Not Assigned') },
                        ...(lookups[field.source]?.items || []).map(u => ({ value: u.id, label: u.visibleName })),
                    ]
                    : [{ value: user?.data?.id, label: t('Assigned Me') }];
                // Çekim çubuğunun birleşik değeri ("-1,<id>" = serbest + bana atanmış) listede yoksa ham metin görünürdü.
                const current = draft[field.key];
                if (`${current ?? ''}`.includes(','))
                    options.unshift({ value: current, label: t('QF_ASSIGN_FREE_OR_MINE') });

                return (
                    <ResponsiveSelect
                        {...selectProps(field)}
                        showSearch
                        options={options}
                        filterOption={(input, option) => matchesSearch(option?.label, input)}
                    />
                );
            }
            default:
                return (
                    <ResponsiveSelect
                        {...selectProps(field)}
                        mode={field.multiple ? 'multiple' : undefined}
                        options={[
                            ...(field.allOption ? [{ value: ALL, label: t('All') }] : []),
                            ...field.options.map(o => ({ value: o.value, label: t(o.label) })),
                        ]}
                    />
                );
        }
    };

    return (
        <div className="qf-panel">
            <Input
                size={controlSize}
                allowClear
                autoFocus={!isMobile}
                prefix={<SearchOutlined aria-hidden="true" />}
                placeholder={t('QF_SEARCH_FILTERS')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="qf-panel-search"
            />
            <div className="qf-panel-body">
                {sections.length === 0 && <div className="qf-panel-empty">{t('QF_NO_MATCHING_FILTER')}</div>}
                {sections.map(section => {
                    const activeCount = section.fields.filter(isDraftActive).length;
                    return (
                        <section key={section.key || 'fields'} className="qf-panel-group">
                            {section.key && (
                                <div className="qf-panel-group-title">
                                    {t(section.key)}
                                    {activeCount > 0 && <span className="qf-panel-group-count">{activeCount}</span>}
                                </div>
                            )}
                            <div className="qf-panel-grid">
                                {section.fields.map(field => (
                                    <div
                                        key={field.key}
                                        className={`qf-panel-field${isDraftActive(field) ? ' is-active' : ''}`}
                                    >
                                        <span className="qf-panel-label">
                                            {labelText(field)}
                                            {isAtDefault(field) && <span className="qf-panel-default"> · {t('Default')}</span>}
                                        </span>
                                        {renderControl(field)}
                                    </div>
                                ))}
                            </div>
                        </section>
                    );
                })}
            </div>
            <div className="qf-panel-actions">
                <Button size={controlSize} onClick={clearDraft}>{t('Clear')}</Button>
                <Button size={controlSize} type="primary" onClick={applyDraft}>{t('Apply')}</Button>
            </div>
        </div>
    );
}

/**
 * "Diğer filtreler" düğmesi + paneli. Panel taslak üzerinde çalışır, "Uygula" ile tek istekte uygulanır
 * (her seçimde grid'i yeniden sorgulamasın diye). activeCount: düğmede gösterilen etkin filtre sayısı.
 *
 * content HER ZAMAN verilir: antd Tooltip/Popover içeriği boşsa açılmaz VE onOpenChange'i hiç çağırmaz
 * (bkz. antd tooltip "noTitle") — "yalnız açıkken çiz" koşulu düğmeyi tamamen kilitliyordu.
 * Paneli yalnız açıkken yaşatan destroyOnHidden: seçim kutularının listeleri panel açılınca çekilir,
 * kapanınca panel sökülür ve bir sonraki açılışta taslak uygulanan filtreden yeniden kurulur.
 *
 * Dar ekranda (MOBILE_BREAKPOINT) panel ekran genişliğinde bir modalda açılır: Popover düğmenin altına
 * sabitlendiği için klavye açılınca alanlar görünmüyor, sayfa kaydırılınca da yerinden kayıyordu.
 */
export function ExtraFiltersButton({ fields, filter, apply, activeCount }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const isMobile = useIsSmallScreen(MOBILE_BREAKPOINT);

    const panel = <ExtraFiltersPanel fields={fields} filter={filter} apply={apply} onDone={() => setOpen(false)} />;

    const button = (
        <button
            type="button"
            className="qf-chip qf-chip-more"
            aria-pressed={activeCount > 0}
            aria-expanded={open}
            // Popover tıklamayı kendisi yönetir; modal ise düğmeden açılır.
            onClick={isMobile ? () => setOpen(true) : undefined}
        >
            <FilterOutlined aria-hidden="true" />
            {t('Other filters')}
            {activeCount > 0 && <span className="qf-count">{activeCount}</span>}
            <DownOutlined aria-hidden="true" />
        </button>
    );

    if (isMobile) {
        return (
            <>
                {button}
                {/* Konum ModalSelect ile aynı kuralda: üstten 10px, kenarlardan 10px. */}
                <Modal
                    open={open}
                    title={t('Other filters')}
                    onCancel={() => setOpen(false)}
                    footer={null}
                    destroyOnHidden
                    width="100%"
                    style={{ top: 10, margin: 10, maxWidth: 'calc(100% - 20px)' }}
                    className="qf-panel-modal"
                    styles={{ body: { paddingTop: 8 } }}
                >
                    {panel}
                </Modal>
            </>
        );
    }

    return (
        <Popover
            trigger="click"
            placement="bottomLeft"
            open={open}
            onOpenChange={setOpen}
            destroyOnHidden
            content={panel}
        >
            {button}
        </Popover>
    );
}
